import React from 'react';
import {
	extractTextContent,
	nullSafeGet,
	nullSafeGetOrElse,
	getWorkOrderTimeZoneId,
} from '../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import WorkOrderDisplayStatusDisplay, {
	getStatusDisplayName,
} from '../work_order_display_status_display/WorkOrderDisplayStatusDisplay';
import {
	getWorkOrderBasePriorityBackgroundColor,
	getWorkOrderBasePriorityPillBackgroundColor,
	isDatePastDue,
} from '../../utils/DataFormatterUtils';
import warrantyDisplay from '../common/WarrantyDisplay';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import DateTimeHover from '../date_time_component/DateTime';
import { WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';
import PrivateWorkOrderLock from '../private_work_order_lock/PrivateWorkOrderLock';
import { connect } from 'react-redux';
import {
	getBuyerOrContractorNameWithHighlightKey,
	getSanitisedWorkOrder,
} from '../../utils/WorkOrderUtils';
import { Popover } from 'antd';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import WorkOrderTechAssignedPill from '../work_order_tech_assigned_pill/WorkOrderTechAssignedPill';
import WorkOrderRowNotePreview from './work_order_row_note_preview/WorkOrderRowNotePreview';

const moment = require('moment');
require('./WorkOrderRowDisplay.less');

const renderWithHighlight = (highlight, keys, defaultValue, shouldMatchDefaultValue = false) => {
	const highlightValue = highlight && highlight[keys];

	const isMatchingWithDefaultValue =
		highlightValue && highlightValue[0] && shouldMatchDefaultValue
			? defaultValue.includes(highlightValue[0].replace(/<\/?em>/g, ''))
			: true;

	if (highlightValue && isMatchingWithDefaultValue) {
		return (
			<span className="highlights">
				<span dangerouslySetInnerHTML={{ __html: highlightValue[0] }} />
			</span>
		);
	}
	return defaultValue;
};

// these are the fields that are displayed in the work order row. so not needed to show again.
const displayedFields = {
	locationName: 'Location Name',
	assetName: 'Asset Name',
	assetNumber: 'Asset Number',
	woPriorityName: 'Priority',
	problemTypeName: 'Problem Type',
	supplierFacilityDisplayName: 'Supplier Facility',
	id: 'Work Order ID',
	poNumber: 'PO Number',
	dueDate: 'Due Date',
	createdAt: 'Created At',
	estimatedCompletionDate: 'Estimated Completion Date',
	title: 'Title',
	notes: 'Notes',
	problemTypeHierarchyName: 'Problem Type',
	buyerProjectTitles: 'Buyer Projects',
};

const highlightLabels = {
	description: 'Description',
	notes: 'Notes',
	serviceCallCheckOutNotes: 'Check Out Notes',
	serviceCallCheckInNotes: 'Check In Notes',
	poNumber: 'PO Number',
	buyerSpecificId: 'Buyer Specific ID',
	woNumber: 'Work Order Number ',
	assetTypeName: 'Asset Type',
	assetSerialNumber: 'Asset Serial Number',
	assetEquipmentUniqueId: 'Asset Equipment Unique ID',
	assetId: 'Asset ID',
	buyerSpecificAssetId: 'Buyer Specific Asset ID',
};

const WorkOrderRowDisplay: React.FC<any> = ({
	workOrder,
	simpler,
	currentUser,
	userType,
	hideDueDate = false,
	titleActions = null,
	showFullTitle = false,
	targetCollectionName = null,
	refreshTable,
}) => {
	if (!workOrder) {
		return null;
	}

	const hasEstimatedCompletionDate = !!workOrder.estimatedCompletionDate;
	const isDueDateSameAsCompletionDate = hasEstimatedCompletionDate
		? moment(workOrder.dueDate).diff(workOrder.estimatedCompletionDate, 'hours') === 0
		: true;
	const isPastDue = isDatePastDue(workOrder.dueDate);

	const isPastEstimatedCompletionDate =
		isPastDue && isDueDateSameAsCompletionDate
			? true
			: moment(workOrder.estimatedCompletionDate).diff(moment(), 'milliseconds') < 0;

	const isNotClosed =
		workOrder.displayStatus !== 'Completed' && workOrder.displayStatus !== 'Cancelled';
	const { highlight } = workOrder;

	const buyerAttachments = nullSafeGetOrElse('buyerAttachments', workOrder, []);
	const supplierAttachments = nullSafeGetOrElse('supplierAttachments', workOrder, []);

	const totalAttachments = buyerAttachments.length + supplierAttachments.length;
	return (
		<div className="rowColumn">
			<div className="rowPrimary">
				<PrivateWorkOrderLock
					workOrder={workOrder}
					style={{ marginTop: -6, marginRight: 8, color: 'red' }}
					fontSize={16}
				/>
				<Ellipsis
					tooltip={true}
					fullWidthRecognition={true}
					lines={showFullTitle ? undefined : 1}
					style={{
						color: 'rgba(0,0,0,0.65)',
						paddingBottom: '1px',
						marginRight: '5%',
						overflow: 'hidden',
						lineHeight: 1.5,
						wordBreak: 'break-word',
						whiteSpace: 'normal',
					}}
				>
					{renderWithHighlight(
						workOrder.highlight,
						'title',
						getSanitisedWorkOrder(workOrder).title
					)}
					{titleActions && titleActions(workOrder)}
				</Ellipsis>
			</div>
			<div className="rowSecondary">
				<div className="rowInlineGroup">
					<span className="rowGrayChip">
						<i className="icons8-font icons8-location-marker" />
						{renderWithHighlight(
							workOrder.highlight,
							'locationName',
							nullSafeGet('location.name', getSanitisedWorkOrder(workOrder))
						)}
					</span>
				</div>
				{nullSafeGet('asset.name', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-bulldozer" />
							{renderWithHighlight(
								workOrder.highlight,
								'assetName',
								nullSafeGet('asset.name', workOrder)
							)}
							{nullSafeGet('asset.assetNumber', workOrder) && (
								<span>
									{' '}
									#
									{renderWithHighlight(
										workOrder.highlight,
										'assetNumber',
										nullSafeGet('asset.assetNumber', workOrder)
									)}
								</span>
							)}
						</span>
					</div>
				) : null}
				{nullSafeGet('isWarranty', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						{warrantyDisplay('Active')}
					</div>
				) : null}
				<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
					<WorkOrderDisplayStatusDisplay status={getSanitisedWorkOrder(workOrder).displayStatus} />
				</div>
				<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
					<span
						style={{
							color: getWorkOrderBasePriorityBackgroundColor(
								getSanitisedWorkOrder(workOrder).woPriority
							),
							backgroundColor: getWorkOrderBasePriorityPillBackgroundColor(
								getSanitisedWorkOrder(workOrder).woPriority
							),
							padding: '4px 12px',
							borderRadius: 16,
						}}
					>
						{renderWithHighlight(
							workOrder.highlight,
							'woPriorityName',
							nullSafeGet('woPriority.name', getSanitisedWorkOrder(workOrder))
						)}
					</span>
				</div>
				<WorkOrderTechAssignedPill workOrder={workOrder} />
				{workOrder.isCapex ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span
							className={'rowGrayChip'}
							style={{
								padding: '4px 12px',
								borderRadius: 16,
							}}
						>
							CapEx
						</span>
					</div>
				) : null}
				<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
					<i className="icons8-font icons8-maintenance-filled" />
					<span>
						{renderWithHighlight(
							workOrder.highlight,
							'problemTypeHierarchyName',
							nullSafeGetOrElse(
								'problemType.hierarchyName',
								getSanitisedWorkOrder(workOrder),
								nullSafeGet('problemType.name', getSanitisedWorkOrder(workOrder))
							)
						)}
					</span>
				</div>
				{getBuyerOrContractorNameWithHighlightKey(workOrder, currentUser) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<i className="icons8-font icons8-company" />
						{renderWithHighlight(
							workOrder.highlight,
							getBuyerOrContractorNameWithHighlightKey(workOrder, currentUser).key,
							getBuyerOrContractorNameWithHighlightKey(workOrder, currentUser).value,
							true
						)}
					</div>
				) : null}
				<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
					<span>
						WO #{renderWithHighlight(workOrder.highlight, 'id', nullSafeGet('id', workOrder))}
					</span>
				</div>

				{totalAttachments > 0 ? (
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<Popover
							title="Attachments"
							content={
								<div className="attachmentsPopover">
									{buyerAttachments.length > 0 && (
										<div className="flex flex-row items-center justify-between gap-x-6">
											<div>{userType === ROLE_TYPES.BUYER ? 'Your' : 'Customer'} Attachments</div>
											<span className="text-sm text-gray-500">{buyerAttachments.length}</span>
										</div>
									)}
									{supplierAttachments.length > 0 && (
										<div className="flex flex-row items-center justify-between gap-x-6">
											<div>{userType === ROLE_TYPES.BUYER ? 'Supplier' : 'Your'} Attachments</div>
											<span className="text-sm text-gray-500">{supplierAttachments.length}</span>
										</div>
									)}
								</div>
							}
						>
							<span className="rowGrayChip">
								<i className="icons8-font icons8-attachment" />
								{totalAttachments} Attachment
								{totalAttachments > 1 ? 's' : ''}
							</span>
						</Popover>
					</div>
				) : null}
				{nullSafeGet('subContractedToWorkOrder.subSupplierFacilityId', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<span className="rowGrayChip">
							Subcontracted:{' '}
							{getStatusDisplayName(
								nullSafeGet('subContractedToWorkOrder.contractorDisplayStatus', workOrder)
							)}
						</span>
					</div>
				) : null}
				{nullSafeGet('poNumber', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<span>
							PO #
							{renderWithHighlight(
								workOrder.highlight,
								'poNumber',
								nullSafeGet('poNumber', workOrder)
							)}
						</span>
					</div>
				) : null}
				{nullSafeGet('buyerProjects.length', workOrder) === 1 ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-blueprint" />
							{renderWithHighlight(
								workOrder.highlight,
								'buyerProjects',
								nullSafeGet('buyerProjects', workOrder)[0].title
							)}
						</span>
					</div>
				) : nullSafeGet('buyerProjects.length', workOrder) > 1 ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-blueprint" />
							{nullSafeGet('buyerProjects.length', workOrder)} Projects
						</span>
					</div>
				) : null}

				{nullSafeGet('linkedToWorkOrders.length', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-link" />
							{nullSafeGet('linkedToWorkOrders.length', workOrder)} Work orders
						</span>
					</div>
				) : null}
			</div>

			{!nullSafeGet('subContractedDetails.length', workOrder) ? (
				<WorkOrderRowNotePreview
					workOrder={workOrder}
					userType={userType}
					targetCollectionName={targetCollectionName}
					refreshTable={refreshTable}
				/>
			) : null}
			<div className="rowTertiary">
				{isNotClosed ? (
					isDueDateSameAsCompletionDate ? (
						isPastDue ? (
							!hideDueDate ? (
								<span
									style={{
										color: 'rgb(163, 40, 40)',
										backgroundColor: 'rgb(249, 230, 230)',
										padding: '4px 12px',
										borderRadius: 16,
									}}
								>
									Past due{' '}
									<DateTimeHover
										timeZone={getWorkOrderTimeZoneId(workOrder)}
										timestamp={workOrder.dueDate}
										showTime={false}
									/>
								</span>
							) : null
						) : (
							<span>
								Reported{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={workOrder.createdAt}
								/>
							</span>
						)
					) : isPastEstimatedCompletionDate ? (
						[
							isPastDue ? (
								!hideDueDate ? (
									<span
										style={{
											color: 'rgb(163, 40, 40)',
											backgroundColor: 'rgb(249, 230, 230)',
											padding: '4px 12px',
											borderRadius: 16,
										}}
									>
										Past due{' '}
										<DateTimeHover
											timeZone={getWorkOrderTimeZoneId(workOrder)}
											timestamp={workOrder.dueDate}
											showTime={false}
										/>
									</span>
								) : null
							) : (
								<span>
									Reported{' '}
									<DateTimeHover
										timeZone={getWorkOrderTimeZoneId(workOrder)}
										timestamp={workOrder.createdAt}
									/>
								</span>
							),
							<span
								style={{
									color: 'rgb(163, 40, 40)',
									backgroundColor: 'rgb(249, 230, 230)',
									padding: '4px 12px',
									borderRadius: 16,
								}}
							>
								Estimated completion{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={nullSafeGet('estimatedCompletionDate', workOrder)}
									showDate
								/>{' '}
							</span>,
						]
					) : (
						[
							<span>
								Reported{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={workOrder.createdAt}
								/>{' '}
							</span>,
							<span>
								Estimated completion{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={workOrder.estimatedCompletionDate}
								/>
							</span>,
						]
					)
				) : (
					<span>
						Reported{' '}
						<DateTimeHover
							timeZone={getWorkOrderTimeZoneId(workOrder)}
							timestamp={workOrder.createdAt}
						/>
					</span>
				)}

				{workOrder.displayStatus === WORK_ORDER_DISPLAY_STATUSES.inProgress &&
				nullSafeGet('lastServiceCall.checkInTime', workOrder) &&
				!nullSafeGet('lastServiceCall.checkOutTime', workOrder) ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<span>
							Checked in at{' '}
							<DateTimeHover
								timeZone={getWorkOrderTimeZoneId(workOrder)}
								timestamp={nullSafeGet('lastServiceCall.checkInTime', workOrder)}
								showDate
							/>
						</span>
					</span>
				) : [
						WORK_ORDER_STATUSES.techScheduled,
						WORK_ORDER_STATUSES.techEnRoute,
						WORK_ORDER_STATUSES.techRescheduled,
						WORK_ORDER_STATUSES.techWorkingOnSite,
				  ].includes(workOrder.status) &&
				  nullSafeGet('lastServiceCall.serviceScheduledAt', workOrder) ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<span>
							Service scheduled{' '}
							<DateTimeHover
								timeZone={getWorkOrderTimeZoneId(workOrder)}
								timestamp={nullSafeGet('lastServiceCall.serviceScheduledAt', workOrder)}
								showDate
							/>
						</span>
					</span>
				) : null}

				{workOrder.displayStatus === WORK_ORDER_DISPLAY_STATUSES.complete &&
				nullSafeGet('completedAt', workOrder) ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<span>
							Completed at{' '}
							<DateTimeHover
								timeZone={getWorkOrderTimeZoneId(workOrder)}
								timestamp={nullSafeGet('completedAt', workOrder)}
								showTime={false}
							/>
						</span>
					</span>
				) : null}
			</div>
			{highlight &&
				Object.keys(highlight).map((key) => {
					if (!displayedFields[key] && highlightLabels[key]) {
						return (
							<div className="rowInlineGroup   ">
								<span className="highlights flex flex-col text-gray-500">
									<span
										style={{
											marginBottom: 4,
											background: 'white',
											border: 'none',
											fontSize: 14,
											textTransform: 'capitalize',
										}}
									>
										{highlightLabels[key] || key}
									</span>
									<span className="highlights inline-flex flex-wrap gap-2">
										{(highlight[key] || []).map((note, index) => (
											<div key={index} dangerouslySetInnerHTML={{ __html: note }} />
										))}
									</span>
								</span>
							</div>
						);
					}
					return null;
				})}
			{(highlight && highlight.notes && (
				<div className="rowInlineGroup   ">
					<span className="highlights flex flex-col text-gray-500">
						<span
							style={{
								marginBottom: 4,
								background: 'white',
								border: 'none',

								fontSize: 14,
								textTransform: 'capitalize',
							}}
						>
							Notes
						</span>
						<span className="highlights inline-flex flex-wrap gap-2">
							{nullSafeGet(`highlight.notes`, workOrder).map((note, index) => (
								<div className="notes" key={index} dangerouslySetInnerHTML={{ __html: note }} />
							))}
						</span>
					</span>
				</div>
			)) ||
				null}
			{(highlight && highlight.troubleshooting && (
				<div className="rowInlineGroup   ">
					<span className="highlights flex flex-col text-gray-500">
						<span
							style={{
								marginBottom: 4,
								background: 'white',
								border: 'none',
								fontSize: 14,
								textTransform: 'capitalize',
							}}
						>
							Troubleshooting
						</span>
						<span className="highlights flex-col flex-wrap gap-2">
							{nullSafeGet(`highlight.troubleshooting`, workOrder).map((note, index) => (
								<div key={index} className="mb-3 text-base">
									{nullSafeGet('question', note)
										? extractTextContent(nullSafeGet('question', note))
										: null}
									{nullSafeGet('answer', note) ? (
										<div
											className="ml-2 text-base "
											dangerouslySetInnerHTML={{
												__html: nullSafeGet('answer', note),
											}}
										/>
									) : null}
								</div>
							))}
						</span>
					</span>
				</div>
			)) ||
				null}
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default withUserType(connect(mapStateToProps)(WorkOrderRowDisplay));
